import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LOCALE, APP_NAVIGATION } from "../../utils/constants";
import { useParams, useNavigate } from "react-router-dom";
import { CreateNewUserModel } from "../model/component";
import { toast } from "react-toastify";
import PathHelper from "../../utils/path-helper";

export const InviteFriend = ({
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
  userId,
  userEmail,
  onInviteComplete,
  className = "",
  buttonText,
  buttonClassName = "",
  selectedPlan,
  toggleFilterPanel,
  // New props for section mode
  sectionMode = false,
  assessmentData,
  showRemainingInvites = true,
  bgColor,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPremiumOrGuest, setIsPremiumOrGuest] = useState(false);
  const [billingDetails, setBillingDetails] = useState(null);
  const [guestUserCount, setGuestUserCount] = useState(0);
  const [guestUserData, setGuestUserData] = useState([]);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [transactionDetails, setTransactionDetail] = useState(null);
  const [skuId, setSkuId] = useState("");
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      checkUserStatus();
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (transactionDetails && transactionDetails?.id) {
      let skuGuid = selectedPlan?.id;
      if (!skuGuid) {
        skuGuid = skuId;
      }
      const data = {
        skuGuid: skuGuid,
        transId: transactionDetails?.id,
        locale: "en",
      };
      getBillingConnect(data).then((res) => {
        setBillingDetails(res);
      });
    }
  }, [transactionDetails, selectedPlan?.id, skuId, getBillingConnect]);

  const checkUserStatus = async () => {
    setIsLoading(true);
    try {
      const [guestStatus, guestData] = await Promise.all([
        isGuestOrPremiumUserConnect(userId),
        getGuestUserConnect(userId),
      ]);

      setIsPremiumOrGuest(guestStatus?.isPremiumOrGuestUser);

      if (guestData) {
        setGuestUserData(guestData);
        const filteredData = guestData.filter(
          (item) => item.invitationStatus === 0 || item.invitationStatus === 1
        );
        setGuestUserCount(filteredData.length);
      }

      getTransactionDetailConnect(userId).then((res) => {
        if (res && Object.keys(res).length > 0) {
          const gatewayRequest = res.gatewayRequest;
          setTransactionDetail(res);
          try {
            const jsonReq = JSON.parse(gatewayRequest);
            setSkuId(jsonReq?.SKUGuid ?? null);
          } catch (error) {
            setSkuId(null); // optional fallback
          }
        } else {
          setSkuId(null);
        }
      });
    } catch (error) {
      console.error("Error checking user status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteFriendClick = () => {
    if (isPremiumOrGuest) {
      if (guestUserCount < billingDetails?.maxSubUserCount) {
        setOpenCreateUser(true);
        if (toggleFilterPanel) {
          toggleFilterPanel();
        }
      } else {
        toast.warning(t("settings.max_user_reached_msg"));
      }
    } else {
      const path = PathHelper(APP_NAVIGATION.PLAN, currentLocale);
      navigate(`${path.next}`);
    }
  };

  if (!userId) {
    return (
      <div className={`text-center text-gray-500 ${className}`}>
        {t("dashboard.invite_friend_cta")}
      </div>
    );
  }

  // Section mode - renders as a complete section with background and styling
  if (sectionMode) {
    const message = t("report.common.assessment_completed_message");
    const finalMessage = message.replace(
      "{assessmentName}",
      assessmentData?.assessmentTitle || "Assessment"
    );

    return (
      <section className={`space-y-6 my-2 ${className}`}>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div
            className={`bg-gradient-to-r ${bgColor} p-8 flex flex-col justify-center items-center gap-2`}
          >
            <h3 className="text-xl font-bold text-white text-center">
              {finalMessage}
            </h3>
            {showRemainingInvites && (
              <p className="text-white opacity-90 text-center">
                {t("dashboard.remaining_invites_text")}{" "}
                {parseInt(billingDetails?.maxSubUserCount) -
                  parseInt(guestUserCount) || 0}{" "}
                {t("dashboard.invite_friend_info")}
              </p>
            )}
            <button
              onClick={handleInviteFriendClick}
              disabled={isLoading}
              className={
                buttonClassName ||
                `bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit
                  ${isLoading ? "animate-pulse" : ""}`
              }
            >
              {buttonText || t("dashboard.invite_friend_cta")}
            </button>

            <CreateNewUserModel
              id="invite-friend-create-user-modal"
              isOpen={openCreateUser}
              setOpen={setOpenCreateUser}
              parentId={userId}
              CreateGuestUserConnect={guestUserChangesConnect}
              GetGuestUserConnect={getGuestUserConnect}
              setGuestUserData={setGuestUserData}
              setGuestUserCount={setGuestUserCount}
              parentEmail={userEmail}
              guestUserData={guestUserData}
              currentLocale={currentLocale}
              t={t}
              type="create_guest_user"
              isPremiumOrGuest={isPremiumOrGuest}
              guestUserCount={guestUserCount}
            />
          </div>
        </div>
      </section>
    );
  }

  // Default button mode
  return (
    <div className={`invite-friend-component ${className}`}>
      <button
        onClick={handleInviteFriendClick}
        disabled={isLoading}
        className={
          buttonClassName ||
          `bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit
            ${isLoading ? "animate-pulse" : ""}`
        }
      >
        {buttonText || t("dashboard.invite_friend_cta")}
      </button>

      <CreateNewUserModel
        id="invite-friend-create-user-modal"
        isOpen={openCreateUser}
        setOpen={setOpenCreateUser}
        parentId={userId}
        CreateGuestUserConnect={guestUserChangesConnect}
        GetGuestUserConnect={getGuestUserConnect}
        setGuestUserData={setGuestUserData}
        setGuestUserCount={setGuestUserCount}
        parentEmail={userEmail}
        guestUserData={guestUserData}
        currentLocale={currentLocale}
        t={t}
        type="create_guest_user"
        isPremiumOrGuest={isPremiumOrGuest}
        guestUserCount={guestUserCount}
      />
    </div>
  );
};
